<template>
  <v-container
    fluid>
    <span class="base-title my-4 ml-3">
      Documentos de Movimentação
    </span>

    <v-container
      fluid
      class="py-0 px-2">
      <DefaultForm
        ref="MovementDocumentForm"
        :formConfig="formConfig"
        :model="form"
      />
    </v-container>

    <v-container
      v-if="isConditionalMandatory"
      fluid
      class="py-0 px-2 mt-2">
      <v-card class="document-conditions-card">
        <v-row align="center">
          <v-col cols="12" md="8">
            <div class="base-title document-conditions-title">
              Condições do documento
            </div>
          </v-col>

          <v-col cols="12" md="4">
            <v-select
              v-model="form.conditionLayout"
              :items="conditionLayoutOptions"
              item-text="label"
              item-value="value"
              label="Layout das condições"
              outlined
              dense
              hide-details
            />
          </v-col>
        </v-row>

        <v-progress-linear
          v-if="loadingConditionMetadata"
          indeterminate
          color="primary"
          class="my-4"
        />

        <template v-else>
          <template v-if="form.conditionLayout === 'classic'">
            <v-row
              v-for="(condition, i) in form.conditions"
              :key="`classic-${condition.id || i}`"
              :class="{ 'condition-row--invalid': invalidConditionIds.includes(condition.id) }">
              <Condition
                :condition="condition"
                :defaultValue.sync="documentConditionDefaultValue"
                :title="`Condição ${i + 1}`"
                @on-remove="removeCondition"
              />
            </v-row>
          </template>

          <template v-else>
            <div class="compact-conditions-layout">
              <draggable
                v-model="form.conditions"
                handle=".compact-condition-editor__drag-handle--root"
                ghost-class="compact-condition-editor--ghost"
                :animation="180"
                @change="reorderConditions(form.conditions)"
                @end="reorderConditions(form.conditions)">
              <div
                v-for="(condition, i) in form.conditions"
                :key="`compact-${condition.id || 'new'}-${i}`"
                :class="{ 'condition-row--invalid': invalidConditionIds.includes(condition.id) }">
                <CompactConditionEditor
                  :condition="condition"
                  :index="i"
                  :is-first-condition="i === 0"
                  layout="compact"
                  :condition-types="conditionTypeOptions"
                  :datasource="datasource"
                  :field-types="fieldTypes"
                  :field-functions="fieldFunctions"
                  :constants="constants"
                  :operators="operators"
                  :default-value="documentConditionDefaultValue"
                  :can-remove="form.conditions.length > 1"
                  drag-handle-class="compact-condition-editor__drag-handle--root"
                  @remove="removeCondition"
                />
              </div>
              </draggable>
            </div>
          </template>

          <v-row class="mt-5">
            <v-col class="d-flex flex-wrap">
              <v-btn
                color="primary"
                class="mr-3 mb-2"
                @click="addCondition"
                min-height="44">
                <v-icon small left>fa-plus</v-icon>
                Nova condição
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </v-card>
    </v-container>

    <v-container
      fluid
      class="py-0">
      <v-row
        class="d-flex justify-end">
        <v-col
          :cols="$vuetify.breakpoint.xsOnly ? 12 : 2">
          <v-btn
            x-large
            block
            :disabled="isSaveDisabled"
            @click="handleForm"
            color="primary">
            Salvar
          </v-btn>
        </v-col>

        <v-col
          :cols="$vuetify.breakpoint.xsOnly ? 12 : 2">
          <v-btn
            :class="{
              'mt-2': $vuetify.breakpoint.xsOnly
            }"
            outlined
            x-large
            block
            @click="back"
            color="primary">
            Voltar
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <SnackbarCustomize
      ref="SnackbarCustomize"
    />

    <Loader
      :overlay="loading"
    />
  </v-container>
</template>

<script>
import MovementRecordsMixin from '@/shared/mixins/movementRecords/movementRecordsMixin';
import MovementDocumentService from '@/services-http/sdi/MovementDocumentService';
import BeneficiaryTypeService from '@/services-http/sdi/BeneficiaryTypeService';
import VerifyRoutesMixin from '@/shared/mixins/routeManagement/verifyRoutesMixin';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import InsuranceCarriersMixin from '@/shared/mixins/sdi/insuranceCarriersMixin';
import FinancialGroupsMixin from '@/shared/mixins/sdi/financialGroupsMixin';
import DefaultForm from '@/components/Default/DefaultForm/DefaultForm.vue';
import DocumentMixin from '@/shared/mixins/document/documentMixin';
import ContractMixin from '@/shared/mixins/sdi/contractsMixin';
import ContractService from '@/services-http/contract/ContractService';
import Loader from '@/components/Loader/Loader.vue';
import Rules from '@/shared/validators/formRules';
import Condition from '@/components/Condition/Condition.vue';
import { Condition as ConditionType, Field } from '@/store/modules/rule/rule-types';
import draggable from 'vuedraggable';
import CompactConditionEditor from '@/components/Condition/CompactConditionEditor.vue';
import {
  MOVEMENT_DOCUMENT_MANDATORY,
  normalizeMovementDocumentMandatory,
} from '@/shared/utils/movementDocumentMandatory';

export default {
  name: 'MovementDocument',

  components: {
    SnackbarCustomize,
    DefaultForm,
    Condition,
    CompactConditionEditor,
    draggable,
    Loader,
  },

  data: () => ({
    loading: false,
    form: {
      financialGroupId: null,
      insuranceCarrierId: null,
      parentContractId: null,
      documentType: null,
      movementTypes: null,
      beneficiaryTypes: null,
      benefitTypes: [],
      kinshipIds: [],
      contractTypes: [],
      gracePeriod: false,
      mandatoryType: MOVEMENT_DOCUMENT_MANDATORY.NO,
      conditions: [],
      conditionLayout: 'compact',
      isMajority: false,
    },
    financialGroups: [],
    kinships: [],
    documentTypes: [],
    insuranceCarriers: [],
    contracts: [],
    formContracts: [],
    contractTypes: [
      { id: 'all', value: 'Todos' },
      { id: 'Coletivo Empresarial', value: 'Coletivo Empresarial' },
      { id: 'Coletivo por Adesão', value: 'Coletivo por Adesão' },
      { id: 'PME', value: 'PME' },
    ],
    movementDocumentService: null,
    beneficiaryTypeService: null,
    rule: null,
    create: false,
    loadContracts: false,
    conditionalMandatoryValue: MOVEMENT_DOCUMENT_MANDATORY.CONDITIONAL,
    documentConditionDefaultValue: '',
    loadingConditionMetadata: false,
    conditionMetadataLoaded: false,
    conditionMetadataPromise: null,
    conditionLayoutOptions: [
      { label: 'Blocos compactos', value: 'compact' },
      { label: 'Clássico', value: 'classic' },
    ],
    invalidConditionIds: [],
  }),

  mixins: [
    InsuranceCarriersMixin,
    FinancialGroupsMixin,
    MovementRecordsMixin,
    VerifyRoutesMixin,
    DocumentMixin,
    ContractMixin,
  ],

  watch: {
    'form.insuranceCarrierId': {
      immediate: true,
      deep: true,
      async handler(insuranceCarrierId) {
        if (!insuranceCarrierId) return;

        this.findContracts();

        if (this.form.parentContractId) return;

        await this.loadBenefitsByCarrierId(insuranceCarrierId);
      },
    },

    'form.parentContractId': {
      immediate: true,
      deep: true,
      async handler(parentContractId) {
        if (!parentContractId) return;

        await this.loadBenefitsByContractId(parentContractId);
      },
    },

    contracts: {
      immediate: true,
      deep: true,
      handler() {
        this.findContracts();
      },
    },

    'form.mandatoryType': {
      immediate: true,
      async handler(mandatoryType) {
        if (mandatoryType === this.conditionalMandatoryValue) {
          await this.prepareConditionalConditions();
        }
      },
    },
  },

  methods: {
    back() {
      this.$refs.MovementDocumentForm.reset();
      this.$refs.MovementDocumentForm.resetValidation();
      this.redirectRouter('MovementDocument', { isRHProtegido: this.isRHProtegido });
    },

    benefitTypesValidation() {
      if (this.form.benefitTypes.some((benefitTypeCode) => benefitTypeCode === 'all')) {
        this.form.benefitTypes = this.benefitTypes
          .filter((benefitType) => benefitType.benefitTypeCode !== 'all')
          .map((item) => item.benefitTypeCode);
      }
    },

    beneficiaryTypesValidation() {
      if (this.form.beneficiaryTypes && this.form.beneficiaryTypes.length === 0) {
        this.form.beneficiaryTypes = null;
      }
    },

    contractValidation() {
      this.form.contractTypes = null;
    },

    contractTypesValidation() {
      if (this.form.contractTypes.some((contractType) => contractType === 'all')) {
        this.form.contractTypes = ['all'];

        this.form.contractTypes = this.contractTypes
          .filter((contractType) => contractType.id !== 'all')
          .map((item) => item.id);
      }
    },

    async findContracts() {
      const contracts = Array.isArray(this.contracts) ? this.contracts : [];

      this.formContracts = contracts.filter(
        (contract) => contract
          && contract.carrier_id
          && contract.carrier_id.id === this.form.insuranceCarrierId,
      );
    },

    async findContractsAndBenefitsByCarrierId() {
      this.benefitTypes = [];
      this.form.benefitTypes = null;

      this.findContracts();

      if (!this.form.insuranceCarrierId) return;

      await this.loadBenefitsByCarrierId(this.form.insuranceCarrierId);
    },

    async findBenefitsByContractId() {
      this.benefitTypes = [];
      this.form.benefitTypes = null;
      this.form.contractTypes = null;

      if (this.form.parentContractId) {
        await this.loadBenefitsByContractId(this.form.parentContractId);
      } else if (this.form.insuranceCarrierId) {
        await this.loadBenefitsByCarrierId(this.form.insuranceCarrierId);
      }
    },

    formatLists() {
      this.movementTypes.unshift({
        code: 'all',
        description: 'Selecionar todas as opções',
        name: 'Selecionar todos',
        id: 999,
      });

      this.kinships.unshift({
        code: 'all',
        description: 'Selecionar todos',
        id: 'all',
      });

      this.benefitTypes.unshift({
        benefitTypeCode: 'all',
        name: 'Selecionar todos',
        id: 999,
      });
    },

    async prepareConditionalConditions() {
      try {
        await this.loadConditionMetadata();
        this.initializeDocumentConditions();
      } catch (err) {
        if (this.$refs.SnackbarCustomize) {
          this.$refs.SnackbarCustomize.open('error', err);
        }
      }
    },

    loadConditionMetadata() {
      if (this.conditionMetadataLoaded) return Promise.resolve();

      if (this.conditionMetadataPromise) {
        return this.conditionMetadataPromise;
      }

      this.loadingConditionMetadata = true;

      this.conditionMetadataPromise = Promise.all([
        this.$store.dispatch('rule/getConditionTypes'),
        this.$store.dispatch('rule/getDatasources'),
        this.$store.dispatch('rule/getFieldTypes'),
        this.$store.dispatch('rule/getFieldFunctions'),
        this.$store.dispatch('rule/getFieldConstants'),
        this.$store.dispatch('rule/getOperators'),
      ])
        .then(() => {
          this.conditionMetadataLoaded = true;
        })
        .finally(() => {
          this.loadingConditionMetadata = false;
          this.conditionMetadataPromise = null;
        });

      return this.conditionMetadataPromise;
    },

    initializeDocumentConditions() {
      this.form.conditionLayout = this.normalizeConditionLayout(this.form.conditionLayout);

      if (!Array.isArray(this.form.conditions) || !this.form.conditions.length) {
        this.$set(this.form, 'conditions', [this.createCondition(1)]);

        return;
      }

      this.normalizeConditions(this.form.conditions);
    },

    normalizeConditionLayout(conditionLayout) {
      if (conditionLayout === 'compact' || conditionLayout === 'classic') {
        return conditionLayout;
      }

      return 'classic';
    },

    createCondition(order, isSubCondition = false) {
      const condition = new ConditionType();
      condition.id = order;
      condition.order = order;
      condition.isSubCondition = isSubCondition;
      condition.fields = [new Field(1), new Field(2)];

      return condition;
    },

    normalizeConditions(conditions, isSubCondition = false) {
      conditions.forEach((condition, index) => {
        this.ensureConditionShape(condition, index + 1, isSubCondition);
      });
    },

    ensureConditionShape(condition, order, isSubCondition = false) {
      if (!condition.id) this.$set(condition, 'id', order);
      if (!condition.order) this.$set(condition, 'order', order);
      if (isSubCondition || !condition.type) this.$set(condition, 'type', 'AND');
      if (!condition.operator) this.$set(condition, 'operator', '=');
      if (!condition.dataSource) this.$set(condition, 'dataSource', 'registro_movimentacao');
      if (!Array.isArray(condition.children)) this.$set(condition, 'children', []);
      this.$set(condition, 'isSubCondition', isSubCondition);
      this.ensureConditionFields(condition);

      if (condition.children.length) {
        this.normalizeConditions(condition.children, true);
      }
    },

    ensureConditionFields(condition) {
      if (!Array.isArray(condition.fields) || !condition.fields.length) {
        this.$set(condition, 'fields', [new Field(1), new Field(2)]);

        return;
      }

      condition.fields.sort((a, b) => a.order - b.order);

      if (!condition.fields.some((field) => field.order === 1)) {
        condition.fields.unshift(new Field(1));
      }

      if (!condition.fields.some((field) => field.order === 2)) {
        condition.fields.push(new Field(2));
      }
    },

    getNextConditionId(conditions) {
      if (!conditions.length) return 1;

      return Math.max(...conditions.map((condition) => condition.id || 0)) + 1;
    },

    addCondition() {
      this.invalidConditionIds = [];
      if (!Array.isArray(this.form.conditions)) {
        this.$set(this.form, 'conditions', []);
      }

      const condition = this.createCondition(this.form.conditions.length + 1);
      condition.id = this.getNextConditionId(this.form.conditions);
      this.form.conditions.push(condition);
    },

    removeCondition(conditionId) {
      const conditions = this.form.conditions
        .filter((condition) => condition.id !== conditionId);

      if (!conditions.length) {
        this.$set(this.form, 'conditions', [this.createCondition(1)]);

        return;
      }

      this.reorderConditions(conditions);
      this.$set(this.form, 'conditions', conditions);
    },

    reorderConditions(conditions) {
      conditions.forEach((condition, index) => {
        this.$set(condition, 'order', index + 1);

        if (index === 0) {
          this.$set(condition, 'type', 'AND');
        }
      });
    },

    handleMandatoryFieldChange() {
      this.invalidConditionIds = [];
      if (this.isConditionalMandatory) {
        this.prepareConditionalConditions();
      }
    },

    isFieldEmpty(field) {
      if (!field.type) return true;
      return !field.value && !(Array.isArray(field.valuein) && field.valuein.some((v) => v.value));
    },

    getOperatorType(operatorValue) {
      const operator = this.operators.find((op) => op.value === operatorValue);
      return operator ? operator.type : null;
    },

    isConditionEmpty(condition) {
      if (!condition.fields || !condition.fields.length) return true;

      const operatorType = this.getOperatorType(condition.operator);

      const field1 = condition.fields.find((f) => f.order === 1);
      if (field1 && this.isFieldEmpty(field1)) return true;

      if (operatorType === 'BINARY' || operatorType === 'TERNARY') {
        const field2 = condition.fields.find((f) => f.order === 2);
        if (field2 && this.isFieldEmpty(field2)) return true;
      }

      if (operatorType === 'TERNARY') {
        const field3 = condition.fields.find((f) => f.order === 3);
        if (field3 && this.isFieldEmpty(field3)) return true;
      }

      return false;
    },

    validateConditions() {
      this.invalidConditionIds = [];
      const invalid = this.form.conditions.filter((c) => this.isConditionEmpty(c));
      if (invalid.length) {
        this.invalidConditionIds = invalid.map((c) => c.id);
        this.$refs.SnackbarCustomize.open('error', 'As condições não foram preenchidas.');
        return false;
      }
      return true;
    },

    async handleForm() {
      if (this.isConditionalMandatory && !this.validateConditions()) return;
      if (!this.$refs.MovementDocumentForm.$refs.form.validate()) return;

      if (this.isConditionalMandatory) {
        await this.prepareConditionalConditions();
      }

      this.loading = true;

      try {
        const payload = this.mountPaylaod(this.form);

        if (this.create === true) {
          await this.movementDocumentService.Post(payload);
        } else {
          payload.id = this.form.id;
          await this.movementDocumentService.Edit(payload);
        }

        this.$refs.SnackbarCustomize.open('success', 'Operação realizada com sucesso!');

        this.redirectRouter('MovementDocument', { isRHProtegido: this.isRHProtegido });
      } catch (err) {
        this.$refs.SnackbarCustomize.open('error', err);
      } finally {
        this.loading = false;
      }
    },

    kinshipValidation() {
      if (this.form.kinshipIds.some((kinshipId) => kinshipId === 'all')) {
        this.form.kinshipIds = this.kinships
          .filter((kinship) => kinship.code !== 'all')
          .map((item) => item.id);
      }
    },

    async loadBeneficiaryTypes() {
      await this.beneficiaryTypeService.FindAll()
        .then((response) => response.data)
        .then((result) => {
          this.beneficiaryTypes = result;
        });
    },

    async loadContractsByFinancialGroupId() {
      if (!this.form.financialGroupId) {
        this.contracts = [];
        return;
      }

      this.loadContracts = true;

      await this.getContractsByFinancialGroupIdVerifyRhProtegido(
        this.form.financialGroupId,
        this.isRHProtegido,
      )
        .finally(() => {
          this.loadContracts = false;
        });
    },

    onFinancialGroupChange() {
      this.form.parentContractId = null;
      this.contracts = [];
      this.loadContractsByFinancialGroupId();
    },

    async fetchFinancialGroupIdByContractId(contractId) {
      if (!contractId) {
        return null;
      }

      const contractService = new ContractService();

      try {
        const response = await contractService.FindById(contractId);

        if (response && response.data && response.data.financial_group_id) {
          return response.data.financial_group_id.id;
        }
      } catch (err) {
        if (this.$refs.SnackbarCustomize) {
          this.$refs.SnackbarCustomize.open('error', err);
        }
      }

      return null;
    },

    async resolveFinancialGroupIdForEdit(routeParams) {
      if (routeParams.financialGroupId) {
        return parseInt(routeParams.financialGroupId, 10);
      }

      const contractId = routeParams.contract && routeParams.contract.id
        ? parseInt(routeParams.contract.id, 10)
        : null;

      if (!contractId) {
        return null;
      }

      return this.fetchFinancialGroupIdByContractId(contractId);
    },

    movementTypesValidation() {
      if (this.form.movementTypes.length === 0) {
        this.form.movementTypes = null;

        return;
      }

      if (this.form.movementTypes.some((movementType) => movementType === 'all')) {
        this.form.movementTypes = this.movementTypes
          .filter((movementType) => movementType.code !== 'all')
          .map((item) => item.code);
      }
    },

    mountPaylaod(form) {
      const kinships = form.kinshipIds.map(
        (kinshipId) => ({ id: kinshipId }),
      );

      return {
        carrier: { id: form.insuranceCarrierId.toString() },
        contract: form.parentContractId
          ? { id: form.parentContractId.toString() }
          : null,
        documentType: { id: form.documentType },
        gracePeriod: form.gracePeriod,
        mandatoryType: normalizeMovementDocumentMandatory(form.mandatoryType),
        conditions: form.mandatoryType === this.conditionalMandatoryValue ? form.conditions : [],
        beneficiaryTypes: form.beneficiaryTypes,
        movementRecordTypes: form.movementTypes,
        benefitTypes: form.benefitTypes,
        kinships,
        contractTypes: !form.contractTypes ? [] : form.contractTypes,
        isMajority: form.isMajority,
      };
    },

    mountForm(form) {
      const kinshipIds = form.kinships
        && form.kinships.length > 0
        ? form.kinships.map((kinship) => kinship.id)
        : [];
      const mandatoryType = normalizeMovementDocumentMandatory(form.mandatoryType);

      const selectedContract = form.contract && form.contract.id ? this.contracts
        .find((contract) => contract.id === parseInt(form.contract.id, 10)) : null;

      const financialGroupIdFromParams = form.financialGroupId
        ? parseInt(form.financialGroupId, 10)
        : null;
      const financialGroupIdFromContract = selectedContract && selectedContract.financial_group_id
        ? selectedContract.financial_group_id.id
        : null;

      this.form = {
        id: form.id,
        insuranceCarrierId: parseInt(form.carrier.id, 10),
        financialGroupId: financialGroupIdFromParams || financialGroupIdFromContract,
        parentContractId: form.contract ? parseInt(form.contract.id, 10) : null,
        documentType: form.documentType.id,
        gracePeriod: form.gracePeriod ? form.gracePeriod : false,
        mandatoryType,
        conditions: form.conditions || [],
        conditionLayout: mandatoryType === this.conditionalMandatoryValue
          ? 'compact'
          : this.normalizeConditionLayout(form.conditionLayout),
        beneficiaryTypes: form.beneficiaryTypes,
        movementTypes: form.movementRecordTypes,
        benefitTypes: form.benefitTypes,
        kinshipIds,
        contractTypes: !form.contractTypes ? [] : form.contractTypes,
        isMajority: form.isMajority || false,
      };
    },

    handlePageRefresh() {
      sessionStorage.setItem('form', JSON.stringify(this.form));
    },

    setSessionStoreData() {
      if (sessionStorage.getItem('form')) {
        const sessionForm = JSON.parse(sessionStorage.getItem('form'));
        const mandatoryType = normalizeMovementDocumentMandatory(sessionForm.mandatoryType);

        this.form = {
          ...this.form,
          ...sessionForm,
          mandatoryType,
          conditions: sessionForm.conditions || [],
          conditionLayout: this.normalizeConditionLayout(sessionForm.conditionLayout),
        };

        sessionStorage.removeItem('form');
      }
    },
  },

  computed: {
    isConditionalMandatory() {
      return this.form.mandatoryType === this.conditionalMandatoryValue;
    },

    isSaveDisabled() {
      if (!this.isConditionalMandatory) return false;
      if (!this.form.conditions || !this.form.conditions.length) return true;
      return this.form.conditions.some((c) => this.isConditionEmpty(c));
    },

    conditionTypeOptions() {
      return this.$store.state.rule.conditionTypes
        .filter((conditionType) => conditionType);
    },

    datasource() {
      return this.$store.state.rule.datasource;
    },

    fieldTypes() {
      return this.$store.state.rule.fieldTypes;
    },

    fieldFunctions() {
      return this.$store.state.rule.fieldFunctions;
    },

    constants() {
      return this.$store.state.rule.fieldConstants;
    },

    operators() {
      return this.$store.state.rule.operators;
    },

    degreeKinships() {
      return this.kinships.filter((kinship) => kinship.id !== 22
        || kinship.code !== 'Titular'
        || kinship.description !== 'Titular');
    },

    formConfig() {
      const propsCol = {
        class: 'text-subtitle-1 primary--text font-weight-medium pb-0',
        cols: '12',
        md: '3',
        lg: '3',
        xl: '3',
      };

      const propsAutocomplete = {
        placeholder: 'Selecione',
        outlined: true,
        clearable: true,
        dense: true,
        color: 'primary--text',
        itemColor: 'primary--text',
      };

      const propsCard = { class: 'rounded-lg pa-7' };

      return [
        {
          title: '',
          attrs: propsCard,
          fields: [
            {
              name: 'insuranceCarrierId',
              type: 'autocomplete',
              col: { attrs: propsCol },
              attrs: {
                ...propsAutocomplete,
                label: 'Operadora*',
                itemText: 'xipp_commercial_name',
                itemValue: 'id',
                items: this.insuranceCarriers,
                rules: [this.rule.required],
              },
              on: { change: () => this.findContractsAndBenefitsByCarrierId() },
            },
            {
              name: 'financialGroupId',
              type: 'autocomplete',
              col: { attrs: propsCol },
              attrs: {
                ...propsAutocomplete,
                label: 'Grupo Financeiro',
                itemText: 'name',
                itemValue: 'id',
                items: this.financialGroups,
              },
              on: { change: () => this.onFinancialGroupChange() },
            },
            {
              name: 'parentContractId',
              type: 'autocomplete',
              col: { attrs: propsCol },
              attrs: {
                ...propsAutocomplete,
                label: 'Contrato',
                itemText: 'name',
                itemValue: 'id',
                items: this.contracts,
                disabled: !this.form.financialGroupId,
                loading: this.loadContracts,
              },
              on: { change: () => this.findBenefitsByContractId() },
            },
            {
              name: 'documentType',
              type: 'autocomplete',
              col: { attrs: propsCol },
              attrs: {
                ...propsAutocomplete,
                itemText: 'name',
                itemValue: 'id',
                items: this.documentTypes,
                rules: [this.rule.required],
                label: 'Tipo de Documento*',
              },
            },
            {
              name: 'movementTypes',
              type: 'autocomplete',
              col: { attrs: propsCol },
              attrs: {
                ...propsAutocomplete,
                multiple: true,
                items: this.movementTypes,
                itemText: 'name',
                itemValue: 'code',
                rules: [this.rule.required],
                label: 'Tipos de Movimentações*',
              },
              on: { change: () => this.movementTypesValidation() },
            },
            {
              name: 'beneficiaryTypes',
              type: 'autocomplete',
              col: { attrs: propsCol },
              attrs: {
                ...propsAutocomplete,
                itemText: 'description',
                itemValue: 'id',
                items: this.beneficiaryTypes,
                multiple: true,
                rules: [this.rule.required],
                label: 'Tipos de Beneficiários*',
              },
            },
            {
              name: 'benefitTypes',
              type: 'autocomplete',
              col: { attrs: propsCol },
              attrs: {
                ...propsAutocomplete,
                multiple: true,
                items: this.benefitTypes,
                itemText: 'name',
                itemValue: 'benefitTypeCode',
                label: 'Tipos de Benefício',
                loading: this.loadingBenefits,
              },
            },
            {
              name: 'kinshipIds',
              type: 'autocomplete',
              col: { attrs: propsCol },
              attrs: {
                ...propsAutocomplete,
                multiple: true,
                items: this.degreeKinships,
                itemText: 'description',
                itemValue: 'id',
                label: 'Graus de Parentesco',
                disabled: !this.form.beneficiaryTypes || !this.form.beneficiaryTypes.some((beneficiaryType) => beneficiaryType === 'DEPENDENT'),
              },
              on: { change: () => this.kinshipValidation() },
            },
            {
              name: 'contractTypes',
              type: 'autocomplete',
              col: { attrs: propsCol },
              attrs: {
                ...propsAutocomplete,
                items: this.contractTypes,
                multiple: true,
                rules: this.form.parentContractId ? [] : [this.rule.required],
                itemText: 'value',
                itemValue: 'id',
                disabled: !!this.form.parentContractId,
                label: 'Portes do Contrato',
              },
              on: { change: () => this.contractTypesValidation() },
            },
            {
              name: 'gracePeriod',
              type: 'select',
              col: { attrs: propsCol },
              attrs: {
                ...propsAutocomplete,
                items: [
                  { id: true, value: 'Sim' },
                  { id: false, value: 'Não' },
                ],
                itemText: 'value',
                itemValue: 'id',
                label: 'Possui Carência',
              },
            },
            {
              name: 'mandatoryType',
              type: 'select',
              col: { attrs: propsCol },
              attrs: {
                ...propsAutocomplete,
                items: [
                  { id: MOVEMENT_DOCUMENT_MANDATORY.YES, value: 'Sim' },
                  { id: MOVEMENT_DOCUMENT_MANDATORY.NO, value: 'Não' },
                  { id: this.conditionalMandatoryValue, value: 'Condicional' },
                ],
                itemText: 'value',
                itemValue: 'id',
                label: 'Obrigatório*',
              },
              on: { change: () => this.handleMandatoryFieldChange() },
            },
            {
              name: 'isMajority',
              type: 'select',
              col: { attrs: propsCol },
              attrs: {
                ...propsAutocomplete,
                items: [
                  { id: true, value: 'Sim' },
                  { id: false, value: 'Não' },
                ],
                itemText: 'value',
                itemValue: 'id',
                label: 'Maioridade',
              },
            },
          ],
        },
      ];
    },
  },

  created() {
    this.isRHProtegido = this.$route.query.isRHProtegido;

    this.movementDocumentService = new MovementDocumentService();
    this.beneficiaryTypeService = new BeneficiaryTypeService();
    this.rule = new Rules();
  },

  beforeMount() {
    window.addEventListener('beforeunload', this.handlePageRefresh);
    this.setSessionStoreData();
  },

  async mounted() {
    this.loading = true;

    await Promise.all([
      this.getInsuranceCarriers(),
      this.loadBeneficiaryTypes(),
      this.getFinancialGroups(),
      this.loadDocumentTypes(),
      this.loadMovementTypes(),
      this.loadKinships(),
    ]);

    this.formatLists();

    if (this.$route.query && this.$route.query.new && this.$route.query.new.toString() === 'true') {
      this.create = true;
    } else if (Object.keys(this.$route.params).length !== 0) {
      const routeParams = { ...this.$route.params };
      const financialGroupId = await this.resolveFinancialGroupIdForEdit(routeParams);

      if (financialGroupId) {
        routeParams.financialGroupId = financialGroupId;
      }

      this.mountForm(routeParams);

      if (this.form.financialGroupId) {
        await this.loadContractsByFinancialGroupId();
      }
    }

    this.loading = false;
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handlePageRefresh);
  },
};
</script>

<style scoped>
.document-conditions-card {
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 24px;
}

.document-conditions-title {
  font-size: 20px;
  font-weight: 600;
}

.compact-conditions-layout {
  background: var(--v-background-base);
  border: 1px solid var(--v-border-base);
  border-radius: 8px;
  margin-top: 20px;
  padding: 14px;
}

.condition-row--invalid {
  border: 2px solid var(--v-error-base);
  border-radius: 6px;
}
</style>
